import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AgentPeople from "@/pages/agent/AgentPeople";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { UserAvatar } from "@/components/common/UserAvatar";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { SourceSelect } from "@/components/common/SourceSelect";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { InlineSpinner } from "@/components/common/Loading";
import { UserPlus, Search, Users, Trash2, SlidersHorizontal, ArrowUp, ArrowDown, ArrowRightLeft, X, Building2 } from "lucide-react";
import { CrmTabs } from "@/components/layout/CrmTabs";
import { PeopleRowMenu, QuickEditDialog, MoveOrgDialog, EditClassificationsDialog, BulkClassifyDialog } from "@/components/people/PeopleActions";
import { AddLeadDialog } from "@/components/recruiting/AddLeadDialog";
import { HelpHint, ACTION_HELP } from "@/components/common/ActionHelp";
import { toast } from "sonner";

const FILTERS = [
  { key: "", label: "All People" },
  { key: "candidate", label: "Candidates" },
  { key: "agent", label: "Agents" },
  { key: "producer", label: "Producers" },
  { key: "protege", label: "Protégés" },
  { key: "agent_staff", label: "Agent Staff" },
  { key: "lead", label: "Leads" },
  { key: "contact", label: "Contacts" },
  { key: "terminated_agent", label: "Terminated Agents" },
  { key: "terminated_prospect", label: "Terminated Prospects" },
  { key: "do_not_contact", label: "Do Not Contact" },
];

const dash = <span className="text-slate-300">—</span>;
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "—");

// Master column registry. sortKey = server-side sort field (null = not sortable). required = always shown.
const COLS = {
  name: { header: "Name", sortKey: "last_name", required: true, render: (r) => (
    <div className="flex items-center gap-3">
      <UserAvatar name={`${r.first_name} ${r.last_name}`} size={32} />
      <div>
        <p className="font-medium text-slate-800">{r.first_name} {r.last_name}
          {r.preferred_name && <span className="text-slate-400 font-normal"> ({r.preferred_name})</span>}</p>
      </div>
    </div>) },
  human_id: { header: "Person ID", sortKey: "human_id", render: (r) => <span className="font-mono text-xs">{r.human_id}</span> },
  agent_code: { header: "Agent Code", sortKey: null, render: (r) => (r.custom_fields?.agent_number ? <span className="font-mono text-xs">{r.custom_fields.agent_number}</span> : dash) },
  agency_contract_version: { header: "Agency Contract Version", sortKey: null, render: (r) => (r.custom_fields?.agency_contract_version ? <span className="font-mono text-xs">{r.custom_fields.agency_contract_version}</span> : dash) },
  appt_date: { header: "Appt Date", sortKey: null, render: (r) => (r.custom_fields?.appointment_date ? <span className="text-xs">{fmtDate(r.custom_fields.appointment_date)}</span> : dash) },
  preferred_name: { header: "Preferred Name", sortKey: "preferred_name", render: (r) => r.preferred_name || dash },
  email: { header: "Email", sortKey: "email", render: (r) => r.email || dash },
  phone: { header: "Phone", sortKey: "phone", render: (r) => r.mobile || r.phone || dash },
  org: { header: "Organization", sortKey: null, render: (r) => r.organization_name || dash },
  classifications: { header: "Classifications", sortKey: null, render: (r) => (
    <div className="flex flex-wrap gap-1">
      {r.classifications?.length ? r.classifications.map((c) => <StatusBadge key={c} status={c} />) : dash}
    </div>) },
  status: { header: "Status", sortKey: "status", render: (r) => <StatusBadge status={r.status} /> },
  updated: { header: "Updated", sortKey: "updated_at", render: (r) => <span className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.updated_at)}</span> },
  job_title: { header: "Job Title", sortKey: "current_title", render: (r) => r.current_title || dash },
  city: { header: "City", sortKey: "city", render: (r) => r.city || dash },
  state: { header: "State", sortKey: "state", render: (r) => r.state || dash },
  postal_code: { header: "ZIP", sortKey: "postal_code", render: (r) => r.postal_code || dash },
  recruiter: { header: "Recruiter", sortKey: "assigned_recruiter_name", render: (r) => r.assigned_recruiter_name || dash },
  source: { header: "Source", sortKey: "source", render: (r) => r.source || dash },
  candidate_type: { header: "Candidate Type", sortKey: "candidate_type", render: (r) => (r.candidate_type_labels?.length
    ? <span className={r.candidate_type_conflict ? "text-amber-700" : ""} title={r.candidate_type_conflict ? "Several current opportunities have different types — review" : r.candidate_type_source === "legacy" ? "From the legacy person field" : undefined}
      data-testid={`ct-${r.id}`}>{r.candidate_type_labels.join(" / ")}{r.candidate_type_conflict ? " ⚠" : ""}</span>
    : <span className="text-slate-400 text-xs" data-testid={`ct-${r.id}`}>Not assigned</span>) },
  created: { header: "Created", sortKey: "created_at", render: (r) => <span className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</span> },
  last_activity: { header: "Last Activity", sortKey: "last_activity_at", render: (r) => <span className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.last_activity_at)}</span> },
};
const COL_ORDER = ["name", "human_id", "preferred_name", "email", "phone", "org", "classifications",
  "agent_code", "agency_contract_version", "appt_date", "status", "updated", "job_title", "city", "state", "postal_code",
  "recruiter", "source", "candidate_type", "created", "last_activity"];
const DEFAULT_PREFS = { columns: ["name", "email", "phone", "org", "classifications", "agent_code", "updated", "status"],
  page_size: 100, sort: "last_name", order: "asc" };
const PAGE_SIZES = [25, 50, 100, 200];

export default function PeopleList() {
  const { isOfficeUser } = useAuth();
  return isOfficeUser ? <AgentPeople /> : <StaffPeopleList />;
}

function StaffPeopleList() {
  const { classification: routeClass } = useParams();
  const navigate = useNavigate();
  const { isManager, isAdmin, isGroupAdmin, isSuper, roles } = useAuth();
  const canCreate = isManager || isAdmin || isGroupAdmin || roles.has("internal_recruiter");
  const canAddLead = isSuper || isAdmin || isManager || isGroupAdmin || roles.has("internal_recruiter");
  const canManage = isManager || isAdmin || roles.has("group_admin");
  const [leadOpen, setLeadOpen] = useState(false);

  const [active, setActive] = useState(routeClass || "");
  const [q, setQ] = useState(() => sessionStorage.getItem("tr_people_q") || "");
  const [orgFilter, setOrgFilter] = useState(() => sessionStorage.getItem("tr_people_org") || "");
  const [orgs, setOrgs] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [candTypes, setCandTypes] = useState([]);
  const [ctFilter, setCtFilter] = useState(() => sessionStorage.getItem("tr_people_ct") || "");
  const [selected, setSelected] = useState(new Set());
  const [quickEdit, setQuickEdit] = useState(null);
  const [moveOrg, setMoveOrg] = useState(null);   // { ids: [...], name }
  const [editClass, setEditClass] = useState(null);
  const [bulkClass, setBulkClass] = useState(null);   // { ids: [...] }
  const [data, setData] = useState({ persons: [], total: 0, page: 1, page_size: 100 });
  const [counts, setCounts] = useState({ all: 0, by_classification: {} });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [prefsReady, setPrefsReady] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);

  useEffect(() => { setActive(routeClass || ""); setPage(1); }, [routeClass]);

  // Quick Create -> Person opens the create dialog via ?create=1.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("create")) { setOpen(true); setSearchParams({}, { replace: true }); }
  }, [searchParams, setSearchParams]);

  // Load per-user + per-account view preferences once.
  useEffect(() => {
    api.get("/my-profile/view-prefs/people").then((r) => {
      const p = r.data.prefs;
      if (p && Array.isArray(p.columns) && p.columns.length) {
        setPrefs({ ...DEFAULT_PREFS, ...p, columns: p.columns.filter((k) => COLS[k]) });
      }
    }).catch(() => {}).finally(() => setPrefsReady(true));
  }, []);

  const persist = useCallback((patch) => {
    setPrefs((p) => { const np = { ...p, ...patch }; api.put("/my-profile/view-prefs/people", np).catch(() => {}); return np; });
  }, []);

  const loadRefs = useCallback(() => {
    Promise.all([api.get("/organizations"), api.get("/classification-types"), api.get("/candidate-types")])
      .then(([o, ct, cand]) => { setOrgs(o.data.organizations || []); setClassTypes(ct.data.items || []); setCandTypes((cand.data.items || []).filter((c) => c.active !== false)); })
      .catch(() => {});
  }, []);
  useEffect(() => { loadRefs(); }, [loadRefs]);
  useEffect(() => { sessionStorage.setItem("tr_people_q", q); }, [q]);
  useEffect(() => { sessionStorage.setItem("tr_people_org", orgFilter); }, [orgFilter]);
  useEffect(() => { sessionStorage.setItem("tr_people_ct", ctFilter); }, [ctFilter]);

  const reqId = useRef(0);
  const load = useCallback(async () => {
    const my = ++reqId.current;
    setLoading(true); setSelected(new Set());
    try {
      const districtOrgId = localStorage.getItem("tr_district") || "";
      const [p, c] = await Promise.all([
        api.get("/persons", { params: { classification: active, q, page, page_size: prefs.page_size,
          sort: prefs.sort, order: prefs.order, district_org_id: districtOrgId, organization_id: orgFilter, candidate_type: ctFilter } }),
        api.get("/persons/counts", { params: { district_org_id: districtOrgId, organization_id: orgFilter } }),
      ]);
      if (my !== reqId.current) return;  // a newer filter/search request superseded this one
      setData(p.data); setCounts(c.data);
    } catch (e) { if (my === reqId.current) toast.error(apiError(e)); }
    finally { if (my === reqId.current) setLoading(false); }
  }, [active, q, page, prefs.page_size, prefs.sort, prefs.order, orgFilter, ctFilter]);

  useEffect(() => { if (prefsReady) load(); }, [load, prefsReady]);
  useEffect(() => {
    const h = () => { setPage(1); load(); };
    window.addEventListener("tr-district-change", h);
    return () => window.removeEventListener("tr-district-change", h);
  }, [load]);

  const toggleSort = (sortKey) => {
    if (!sortKey) return;
    const order = prefs.sort === sortKey && prefs.order === "asc" ? "desc" : "asc";
    setPage(1); persist({ sort: sortKey, order });
  };
  const changePageSize = (n) => { setPage(1); persist({ page_size: Number(n) }); };

  const rows = data.persons;
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someChecked = rows.some((r) => selected.has(r.id));
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allChecked) rows.forEach((r) => n.delete(r.id)); else rows.forEach((r) => n.add(r.id));
    return n;
  });
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const goDetail = (path, section) => navigate(path + (section ? `?section=${section}` : ""));

  const columns = useMemo(() => {
    const keys = ctFilter && !prefs.columns.includes("candidate_type") ? [...prefs.columns, "candidate_type"] : prefs.columns;
    const base = keys.filter((k) => COLS[k]).map((k) => ({
      key: k, header: COLS[k].header, sortKey: COLS[k].sortKey, render: COLS[k].render }));
    const selectCol = {
      key: "__select", sortKey: null, className: "w-10", headerClassName: "w-10",
      header: (<span onClick={(e) => e.stopPropagation()}><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="select-all" aria-label="Select all" /></span>),
      render: (r) => (<span onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleOne(r.id)} data-testid={`select-${r.id}`} /></span>),
    };
    const actionsCol = {
      key: "__actions", sortKey: null, className: "w-10 text-right", header: "",
      render: (r) => (<PeopleRowMenu person={r} canManage={canManage}
        onQuickEdit={setQuickEdit} onEditClass={setEditClass}
        onMoveOrg={(p) => setMoveOrg({ ids: [p.id], name: `${p.first_name} ${p.last_name}` })}
        onNavigate={goDetail} />),
    };
    return [selectCol, ...base, actionsCol];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs.columns, ctFilter, selected, rows, allChecked, canManage]);

  const start = data.total ? (data.page - 1) * data.page_size + 1 : 0;
  const end = Math.min(data.page * data.page_size, data.total);

  return (
    <div>
      <CrmTabs />
      <PageHeader title="People" description="One master record per person, with multiple classifications."
        actions={
          <div className="flex items-center gap-2">
            {canAddLead && (
              <span className="inline-flex items-center gap-0.5">
                <Button data-testid="people-add-lead" className="bg-brand hover:bg-brand-strong text-white" onClick={() => setLeadOpen(true)}>
                  <UserPlus className="w-4 h-4 mr-1.5" /> Add Lead
                </Button>
                <HelpHint kind="lead" testId="help-add-lead" />
              </span>
            )}
            {canCreate && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-person-button" variant="outline" className="border-slate-300">
                    <UserPlus className="w-4 h-4 mr-1.5" /> Add Person
                  </Button>
                </DialogTrigger>
                <CreatePersonDialog onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />
              </Dialog>
            )}
            {canCreate && <HelpHint kind="person" testId="help-add-person" />}
          </div>
        } />
      {(canAddLead || canCreate) && (
        <p className="-mt-3 mb-4 text-xs text-slate-500" data-testid="people-action-guidance">
          Recruiting someone? Use <b className="text-slate-700">Add Lead</b>. Adding a contact or existing team member? Use <b className="text-slate-700">Add Person</b>.
        </p>)}
      {canAddLead && <AddLeadDialog open={leadOpen} onClose={() => setLeadOpen(false)} onCreated={() => { setLeadOpen(false); load(); }} />}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={active || "all"} onValueChange={(v) => navigate(v === "all" ? "/people" : `/people/${v}`)}>
          <SelectTrigger aria-label="Filter by classification" data-testid="people-class-filter"
            className="h-10 w-full sm:w-[260px] bg-[#006090] text-white border-[#006090] font-medium hover:bg-[#004E75] focus:ring-2 focus:ring-[#006090]/40 focus:ring-offset-2 [&>svg]:opacity-100 [&>svg]:text-white [&>svg]:w-5 [&>svg]:h-5">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white max-h-[60vh]">
            {FILTERS.map((f) => (
              <SelectItem key={f.key || "all"} value={f.key || "all"} data-testid={`filter-${f.key || "all"}`} className="py-2.5 text-sm">
                {f.label} ({f.key ? counts.by_classification?.[f.key] ?? 0 : counts.all ?? 0})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={ctFilter || "all"} onValueChange={(v) => { setCtFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger aria-label="Filter by candidate type" data-testid="people-ct-filter"
            className={`h-10 w-full sm:w-[230px] font-medium ${ctFilter ? "border-[#006090] text-[#006090] bg-[#006090]/5" : "bg-white"}`}>
            <SelectValue placeholder="All Candidate Types" />
          </SelectTrigger>
          <SelectContent className="bg-white max-h-[60vh]">
            <SelectItem value="all" data-testid="ct-filter-all" className="py-2.5 text-sm">All Candidate Types</SelectItem>
            {candTypes.map((c) => (
              <SelectItem key={c.key} value={c.key} data-testid={`ct-filter-${c.key}`} className="py-2.5 text-sm">{c.label}</SelectItem>
            ))}
            <SelectItem value="__none__" data-testid="ct-filter-none" className="py-2.5 text-sm">Not assigned</SelectItem>
          </SelectContent>
        </Select>
        {ctFilter && <button data-testid="ct-filter-clear" onClick={() => { setCtFilter(""); setPage(1); }}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"><X className="w-3.5 h-3.5" /> Clear type</button>}
        {isManager && <button data-testid="filter-deleted" onClick={() => navigate("/people/deleted")}
          className="inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-sm font-medium bg-white text-slate-600 border-slate-200 hover:border-rose-300 hover:text-rose-600 sm:ml-auto">
          <Trash2 className="w-3.5 h-3.5" /> Deleted People
        </button>}
      </div>

      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
        <div className="relative w-full sm:max-w-xs sm:flex-1 sm:min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="people-search" type="search" enterKeyHint="search" placeholder="Search name, email, ID…" value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }} className="pl-9 h-10" />
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:ml-auto">
          <Select value={orgFilter || "all"} onValueChange={(v) => { setOrgFilter(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="h-9 w-full sm:w-[220px]" data-testid="org-filter"><Building2 className="w-4 h-4 mr-1.5 text-slate-400" /><SelectValue placeholder="All Organizations" /></SelectTrigger>
            <SelectContent className="bg-white max-h-[320px]">
              <SelectItem value="all">All Organizations</SelectItem>
              {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}{o.people_count != null ? ` (${o.people_count})` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-500 whitespace-nowrap" data-testid="people-range">
            {data.total ? `${start}–${end} of ${data.total}` : "0 results"}
          </span>
          <Select value={String(prefs.page_size)} onValueChange={changePageSize}>
            <SelectTrigger className="h-9 w-[110px]" data-testid="page-size-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setCustomOpen(true)} data-testid="customize-columns" className="h-9">
            <SlidersHorizontal className="w-4 h-4 mr-1.5" /> Columns
          </Button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-4 rounded-lg border border-[#006090]/30 bg-[#006090]/5 px-4 py-2.5" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-slate-700" data-testid="bulk-selected-count">{selected.size} selected</span>
          {canManage && (
            <Button size="sm" variant="outline" data-testid="bulk-move-org"
              onClick={() => setMoveOrg({ ids: [...selected], name: rows.find((r) => selected.has(r.id))?.first_name })}>
              <ArrowRightLeft className="w-4 h-4 mr-1.5" /> Move Organization
            </Button>
          )}
          {canManage && (
            <Button size="sm" variant="outline" data-testid="bulk-edit-class"
              onClick={() => setBulkClass({ ids: [...selected] })}>
              <SlidersHorizontal className="w-4 h-4 mr-1.5" /> Edit Classifications
            </Button>
          )}
          <button onClick={() => setSelected(new Set())} data-testid="bulk-clear" className="ml-auto text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> Clear</button>
        </div>
      )}

      <DataTable columns={columns} rows={data.persons} loading={loading}
        onRowClick={(r) => navigate(`/people/detail/${r.id}`)}
        page={data.page} pageSize={data.page_size} total={data.total} onPageChange={setPage}
        sortField={prefs.sort} sortOrder={prefs.order} onSort={toggleSort}
        empty={(q || ctFilter || orgFilter || active)
          ? <EmptyState icon={Users} title="No people match these filters" description="Try a different search, organization, classification or candidate type." />
          : <EmptyState icon={Users} title="No people yet" description="People you create or import will appear here." />}
        testId="people-table" resizeKey="people-list-columns"
        viewExtra={{ get: () => ({ columns: prefs.columns, sort: prefs.sort, order: prefs.order }),
          apply: (x) => persist({ ...(x.columns?.length ? { columns: x.columns } : {}), ...(x.sort ? { sort: x.sort, order: x.order } : {}) }) }} />

      {customOpen && <CustomizeColumnsDialog prefs={prefs}
        onClose={() => setCustomOpen(false)}
        onApply={(cols) => { persist({ columns: cols }); setCustomOpen(false); }}
        onReset={() => { setPage(1); persist({ ...DEFAULT_PREFS }); setCustomOpen(false); }} />}

      {quickEdit && <QuickEditDialog person={quickEdit} orgs={orgs} classTypes={classTypes}
        onClose={() => setQuickEdit(null)} onSaved={(o) => { if (!o?.silent) setQuickEdit(null); load(); }} />}
      {editClass && <EditClassificationsDialog person={editClass} classTypes={classTypes}
        onClose={() => setEditClass(null)} onSaved={load} />}
      {moveOrg && <MoveOrgDialog personIds={moveOrg.ids} sampleName={moveOrg.name} orgs={orgs}
        onClose={() => setMoveOrg(null)} onDone={() => { load(); loadRefs(); }} />}
      {bulkClass && <BulkClassifyDialog personIds={bulkClass.ids} classTypes={classTypes}
        onClose={() => setBulkClass(null)} onDone={load} />}
    </div>
  );
}

function CustomizeColumnsDialog({ prefs, onClose, onApply, onReset }) {
  // ordered list: selected columns first (in their order), then the rest
  const initial = [...prefs.columns.filter((k) => COLS[k]),
    ...COL_ORDER.filter((k) => !prefs.columns.includes(k))];
  const [order, setOrder] = useState(initial);
  const [visible, setVisible] = useState(new Set(prefs.columns));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order]; [next[i], next[j]] = [next[j], next[i]]; setOrder(next);
  };
  const toggle = (k) => {
    if (k === "name") return; // Name always visible
    setVisible((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  };
  const apply = () => onApply(order.filter((k) => visible.has(k)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="customize-columns-dialog">
        <DialogHeader><DialogTitle>Customize Columns</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Choose columns and reorder them. Name is always shown.</p>
        <div className="space-y-1 max-h-[52vh] overflow-y-auto tr-scroll pr-1">
          {order.map((k, i) => (
            <div key={k} className="flex items-center gap-2 rounded-md border border-slate-100 px-2 py-1.5" data-testid={`col-row-${k}`}>
              <Checkbox checked={visible.has(k)} disabled={k === "name"} onCheckedChange={() => toggle(k)} data-testid={`col-toggle-${k}`} />
              <span className="text-sm text-slate-700 flex-1">{COLS[k].header}{COLS[k].sortKey ? "" : <span className="text-[10px] text-slate-400 ml-1">(no sort)</span>}</span>
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`col-up-${k}`}><ArrowUp className="w-3.5 h-3.5" /></button>
              <button onClick={() => move(i, 1)} disabled={i === order.length - 1} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`col-down-${k}`}><ArrowDown className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onReset} data-testid="columns-reset">Reset to Default</Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={apply} data-testid="columns-apply" className="bg-[#006090] hover:bg-[#004E75]">Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreatePersonDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", preferred_name: "", email: "",
    phone: "", mobile: "", home_city: "", home_state: "", home_country: "", organization_id: "", candidate_type: "", source: "", source_id: "" });
  const [classes, setClasses] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [candTypes, setCandTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dup, setDup] = useState(null);
  const navigate = useNavigate();
  const { isManager: canInvM, isAdmin: canInvA, isGroupAdmin: canInvG } = useAuth();
  const canInvite = canInvM || canInvA || canInvG;
  const scrollRef = useRef(null);
  useEffect(() => { if (dup && scrollRef.current) { scrollRef.current.scrollTop = 0; } }, [dup]);

  useEffect(() => {
    Promise.all([api.get("/organizations"), api.get("/classification-types"), api.get("/candidate-types")])
      .then(([o, ct, cd]) => {
        const list = o.data.organizations || [];
        setOrgs(list); setClassTypes(ct.data.items); setCandTypes(cd.data.items);
        if (list.length === 1) setForm((f) => ({ ...f, organization_id: f.organization_id || list[0].id }));
      })
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleClass = (k) => setClasses((c) => c.includes(k) ? c.filter((x) => x !== k) : [...c, k]);

  const submit = async (force = false) => {
    if (!form.first_name || !form.last_name) return setError("First and last name are required");
    if (!force && (form.email || form.mobile || form.phone)) {
      try {
        const { data } = await api.post("/person-match/check", { first_name: form.first_name, last_name: form.last_name, email: form.email, mobile: form.mobile, phone: form.phone });
        if (data.match) { setError(""); setDup({ ...data.match, exact: data.match.confidence === "confident" }); return; }
      } catch { /* server re-checks on submit */ }
    }
    if (orgs.length > 1 && !form.organization_id) return setError("Choose the Group for this person");
    setError(""); setDup(null); setLoading(true);
    try {
      const payload = { ...form, classifications: classes,
        organization_id: form.organization_id || null,
        candidate_type: form.candidate_type || null, email: form.email || null, force_create: force };
      const res = await api.post("/persons", payload);
      toast.success("Person created", { description: "No recruiting record or login was created.", action: res.data?.id ? { label: "View Person", onClick: () => navigate(`/people/detail/${res.data.id}`) } : undefined });
      onCreated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.duplicate) setDup({ ...detail.duplicate, exact: !!detail.exact });
      else setError(apiError(e));
    } finally { setLoading(false); }
  };

  return (
    <DialogContent className="bg-white max-w-lg" data-testid="create-person-dialog">
      <DialogHeader><DialogTitle>Add Person</DialogTitle></DialogHeader>
      <p className="-mt-2 text-xs text-slate-500" data-testid="add-person-help">{ACTION_HELP.person.long}</p>
      <div ref={scrollRef} className="space-y-3 max-h-[60vh] overflow-y-auto tr-scroll pr-1">
        {error && <p className="text-sm text-rose-600" data-testid="person-error">{error}</p>}
        {dup && <ExistingPersonPanel dup={dup} form={form} classes={classes} classTypes={classTypes} canInvite={canInvite} loading={loading}
          onOpen={(id) => navigate(`/people/detail/${id}`)} onInvite={(d) => navigate(`/admin/users?invite=1&person_id=${d.person_id}&name=${encodeURIComponent(d.name || "")}&email=${encodeURIComponent(d.email || "")}`)}
          onCancel={() => setDup(null)} onForce={() => submit(true)} onAdded={onCreated} />}
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name *"><Input value={form.first_name} onChange={set("first_name")} data-testid="person-first-name" /></Field>
          <Field label="Last name *"><Input value={form.last_name} onChange={set("last_name")} data-testid="person-last-name" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Preferred name"><Input value={form.preferred_name} onChange={set("preferred_name")} data-testid="person-preferred-name" /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={set("email")} data-testid="person-email" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone"><Input value={form.phone} onChange={set("phone")} data-testid="person-phone" /></Field>
          <Field label="Mobile"><Input value={form.mobile} onChange={set("mobile")} data-testid="person-mobile" /></Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Home City"><Input value={form.home_city} onChange={set("home_city")} data-testid="create-home-city" /></Field>
          <Field label="Home State"><Input value={form.home_state} onChange={set("home_state")} /></Field>
          <Field label="Home Country"><Input value={form.home_country} onChange={set("home_country")} /></Field>
        </div>
        <Field label={orgs.length > 1 ? "Group *" : "Group"}>
          <Select value={form.organization_id} onValueChange={(v) => setForm({ ...form, organization_id: v })}>
            <SelectTrigger data-testid="person-org"><SelectValue placeholder="Choose Group" /></SelectTrigger>
            <SelectContent className="bg-white">
              {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} ({o.org_code})</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Source">
          <SourceSelect organizationId={form.organization_id} value={form.source_id} label={form.source} testId="person-source"
            onChange={({ source_id, source }) => setForm({ ...form, source_id, source })}
            onUnavailable={() => setForm((f) => ({ ...f, source_id: "", source: "" }))} />
        </Field>
        <Field label="Candidate Type">
          <Select value={form.candidate_type} onValueChange={(v) => setForm({ ...form, candidate_type: v })}>
            <SelectTrigger data-testid="person-candidate-type"><SelectValue placeholder="None" /></SelectTrigger>
            <SelectContent className="bg-white">
              {candTypes.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Classifications">
          <div className="flex flex-wrap gap-2">
            {classTypes.filter((c) => c.assignable !== false).map((c) => (
              <label key={c.key} className="flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
                <Checkbox checked={classes.includes(c.key)} onCheckedChange={() => toggleClass(c.key)} data-testid={`class-${c.key}`} />
                {c.label}
              </label>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-1.5">Choose <b>Lead</b> if the person hasn't been meaningfully contacted and hasn't shown interest. Choose <b>Candidate</b> if they've been contacted or shown interest/engagement. Candidate Type applies to Candidates.</p>
        </Field>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        {!dup && <Button onClick={() => submit(false)} disabled={loading} data-testid="person-submit" className="bg-[#006090] hover:bg-[#004E75]">
          {loading ? <InlineSpinner /> : "Create Person"}
        </Button>}
      </DialogFooter>
    </DialogContent>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs font-semibold text-slate-700 mb-1.5 block">{label}</Label>{children}</div>;
}

const CONFLICT_FIELDS = [["first_name", "First name"], ["last_name", "Last name"], ["preferred_name", "Preferred name"], ["email", "Email"], ["mobile", "Mobile"]];

function ExistingPersonPanel({ dup: raw, form, classes, classTypes, canInvite, loading, onOpen, onInvite, onCancel, onForce, onAdded }) {
  const [busy, setBusy] = useState(false);
  const [useExisting, setUseExisting] = useState(false);
  const dup = useExisting ? { ...raw, exact: true } : raw;
  const force = () => { if (raw.match_type === "phone" && !window.confirm("The phone number matches an existing Person. Create a separate new Person anyway? The existing record and login are not changed.")) return; onForce(); };
  const label = (k) => classTypes.find((c) => c.key === k)?.label || k;
  if (!dup.person_id) return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="person-duplicate">
      <p className="font-semibold text-amber-800">This person already exists in Top-Recruit</p>
      <p className="text-slate-700 mt-0.5">{dup.message} Ask a manager for access instead of creating a duplicate.</p>
      <Button size="sm" variant="outline" className="mt-2" onClick={onCancel} data-testid="dup-cancel">Back</Button>
    </div>);
  const newCls = classes.filter((c) => !(dup.classifications || []).includes(c));
  const norm = (k, v) => k === "mobile" ? String(v).replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "") : String(v).trim().toLowerCase();
  const conflicts = CONFLICT_FIELDS.filter(([k]) => form[k] && dup[k] && norm(k, form[k]) !== norm(k, dup[k]));
  const addCls = async () => {
    setBusy(true);
    try { for (const c of newCls) { await api.post(`/persons/${dup.person_id}/classifications`, { classification: c }); } toast.success("Classification added to existing Person"); onAdded(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className={`rounded-lg border p-3 text-sm ${dup.exact ? "border-sky-300 bg-sky-50" : "border-amber-300 bg-amber-50"}`} data-testid="person-duplicate">
      <p className="font-semibold text-slate-900" data-testid="person-duplicate-title">{dup.exact ? "This person already exists in Top-Recruit." : "Possible Existing Person"}</p>
      <p className="text-xs text-slate-600">Matched by {dup.match_label}. {dup.exact ? "A new Person will not be created." : "Review before creating a new record."}</p>
      <div className="mt-2 rounded-md bg-white border border-slate-200 p-2.5 space-y-0.5" data-testid="person-duplicate-card">
        <p className="font-medium text-slate-900">{dup.name}{dup.preferred_name ? ` (${dup.preferred_name})` : ""} <span className="text-xs text-slate-400">{dup.human_id}</span></p>
        <p className="text-xs text-slate-600">{dup.email || "—"}{dup.mobile ? ` · ${dup.mobile}` : ""}</p>
        <p className="text-xs text-slate-600" data-testid="person-duplicate-classifications">Current classifications: <b>{(dup.classifications || []).map(label).join(", ") || "None"}</b></p>
        <p className="text-xs text-slate-600">Current Group: <b>{dup.organization_name || "—"}</b> · Login: <b>{dup.has_login ? "Has a Top-Recruit login" : "No login"}</b></p>
      </div>
      {conflicts.length > 0 && (
        <div className="mt-2 text-xs" data-testid="person-duplicate-conflicts">
          <p className="font-semibold text-slate-700">Different from the existing record (not changed):</p>
          {conflicts.map(([k, l]) => <p key={k} className="text-slate-600">{l}: existing <b>{dup[k]}</b> · you entered <b>{form[k]}</b></p>)}
        </div>)}
      <div className="flex flex-wrap gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={() => onOpen(dup.person_id)} data-testid="dup-view">Open Person</Button>
        {newCls.length > 0 && <Button size="sm" className="bg-[#006090] hover:bg-[#004E75]" disabled={busy} onClick={addCls} data-testid="dup-add-classification">Add {newCls.map(label).join(", ")} to existing Person</Button>}
        {canInvite && !dup.has_login && <Button size="sm" variant="outline" onClick={() => onInvite(dup)} data-testid="dup-invite">Invite to Top-Recruit</Button>}
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="dup-cancel">Back to form</Button>
        {!dup.exact && <Button size="sm" className="bg-[#006090] hover:bg-[#004E75]" onClick={() => setUseExisting(true)} data-testid="dup-use-existing">Use Existing Person</Button>}
        {!dup.exact && <Button size="sm" variant="outline" onClick={force} disabled={loading} data-testid="dup-force">Create New Person</Button>}
      </div>
    </div>);
}
